import type {
  FacilitatorGameCallLinkRequest,
  FacilitatorGameCallLinkResponse,
  FacilitatorSprintCallLinkResponse,
  GetFacilitatorLiveSessionMonitorResponse
} from '../../../../pure-js/types/FacilitatorLiveSessionMonitor'
import type { SprintActivityWherebyMeetingRequest } from '../../../../pure-js/types/WherebyTypes'
import type {
  EndSprintActivityForTeamRequest,
  EndSprintActivityForTeamResponse,
  SendTeamToActivityRequest,
  SendTeamToActivityResponse
} from '../../../../pure-js/types/Sprint'
import type { State } from '../../hooks/useAppState'
import { loadCloudFunctionsHandler } from '../handlers'
import { buildMockFacilitatorLiveSessionMonitor } from '../mocks/facilitatorLiveSessionMock'
import type { ApiResponse } from '../types'
import { handleRequest } from './config'

export const getFacilitatorLiveSessionMonitor = async (
  state: State
): Promise<ApiResponse<GetFacilitatorLiveSessionMonitorResponse>> => {
  return handleRequest<ApiResponse<GetFacilitatorLiveSessionMonitorResponse>>(
    async () => ({
      data: buildMockFacilitatorLiveSessionMonitor(),
      success: true
    }),
    async () => {
      try {
        const cloudFunctions = await loadCloudFunctionsHandler()
        return await cloudFunctions.getFacilitatorLiveSessionMonitor(state)
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to get facilitator live session monitor'
        return {
          data: {
            success: false,
            generatedAt: new Date().toISOString(),
            sections: [],
            error: message
          },
          success: false,
          error: message
        }
      }
    }
  )
}

const emptyCallLink = (): FacilitatorGameCallLinkResponse => ({
  success: false,
  roomUrl: '',
  roomName: '',
  teamName: ''
})

export const getFacilitatorGameCallLink = async (
  request: FacilitatorGameCallLinkRequest
): Promise<ApiResponse<FacilitatorGameCallLinkResponse>> => {
  return handleRequest<ApiResponse<FacilitatorGameCallLinkResponse>>(
    async () => ({
      data: {
        success: true,
        roomUrl: 'https://example.whereby.com/mock-room',
        roomName: '/mock-room',
        teamName: 'Mock team'
      },
      success: true
    }),
    async () => {
      try {
        const cloudFunctions = await loadCloudFunctionsHandler()
        return await cloudFunctions.getFacilitatorGameCallLink(request)
      } catch (error: unknown) {
        // The server answers with a typed code (call-room-not-found, …) as the message; the card maps it to copy.
        const message = error instanceof Error ? error.message : 'Failed to get the call link'
        return { data: emptyCallLink(), success: false, error: message }
      }
    }
  )
}

export const getFacilitatorSprintCallLink = async (
  request: SprintActivityWherebyMeetingRequest
): Promise<ApiResponse<FacilitatorSprintCallLinkResponse>> => {
  return handleRequest<ApiResponse<FacilitatorSprintCallLinkResponse>>(
    async () => ({
      data: { success: true, roomUrl: 'https://example.whereby.com/mock-sprint-room', roomName: '/mock-sprint-room' },
      success: true
    }),
    async () => {
      try {
        const cloudFunctions = await loadCloudFunctionsHandler()
        return await cloudFunctions.getFacilitatorSprintCallLink(request)
      } catch (error: unknown) {
        // The server answers with a typed code as the message; the card maps it to copy.
        const message = error instanceof Error ? error.message : 'Failed to get the call link'
        return { data: { success: false, roomUrl: '', roomName: '' }, success: false, error: message }
      }
    }
  )
}

const nothingEnded = (request: EndSprintActivityForTeamRequest): EndSprintActivityForTeamResponse => ({
  ...request,
  ok: false,
  alreadyCompleted: false,
  activityEnded: false,
  closedGameSession: false,
  endedAiDebrief: false,
  sprintCompleted: false
})

export const endSprintActivityForTeam = async (
  request: EndSprintActivityForTeamRequest
): Promise<ApiResponse<EndSprintActivityForTeamResponse>> => {
  return handleRequest<ApiResponse<EndSprintActivityForTeamResponse>>(
    async () => ({ data: { ...nothingEnded(request), ok: true, endedAiDebrief: true }, success: true }),
    async () => {
      try {
        const cloudFunctions = await loadCloudFunctionsHandler()
        return await cloudFunctions.endSprintActivityForTeam(request)
      } catch (error: unknown) {
        // The server answers with a typed code (not-a-facilitator-of-this-sprint, …) as the message.
        const message = error instanceof Error ? error.message : 'Failed to end the activity for the team'
        return { data: nothingEnded(request), success: false, error: message }
      }
    }
  )
}

const nobodySent: SendTeamToActivityResponse = { ok: false, sentCount: 0 }

export const sendTeamToActivity = async (
  request: SendTeamToActivityRequest
): Promise<ApiResponse<SendTeamToActivityResponse>> => {
  return handleRequest<ApiResponse<SendTeamToActivityResponse>>(
    async () => ({ data: { ok: true, sentCount: 3 }, success: true }),
    async () => {
      try {
        const cloudFunctions = await loadCloudFunctionsHandler()
        return await cloudFunctions.sendTeamToActivity(request)
      } catch (error: unknown) {
        // The error's message is its code (activity-has-no-page, …).
        const message = error instanceof Error ? error.message : 'Failed to send the team to the activity'
        return { data: nobodySent, success: false, error: message }
      }
    }
  )
}
